import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

// Keeps a flat set of named values in sync with the URL's query string,
// instead of plain local useState — specifically so list/filter state
// (search text, selected tab, page, sort column, status filter, etc.)
// survives navigating to a detail page and back. React Router unmounts a
// list page when navigating away from it to a detail route; local
// useState resets to its initial value on that remount, which is exactly
// why "Back" used to land on an empty/reset list instead of where the
// admin actually was. Storing the same state in the URL means the
// remounted component reads its last values straight back out of the
// address bar — no separate persistence mechanism needed.
//
// `schema` is { key: { default, parse?(str) => value, serialize?(value) => string } }.
// Define it as a MODULE-LEVEL constant outside your component (not inline
// in the render) so it's a stable reference across renders.
// A value that equals its `default` (or is "", null, or undefined) is
// removed from the URL entirely rather than written out — keeps the
// query string from filling up with values that'd behave identically to
// the key being absent, and keeps "no filters applied" as a clean URL.
export default function useUrlState(schema) {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => {
    const out = {};
    for (const [key, cfg] of Object.entries(schema)) {
      const raw = searchParams.get(key);
      out[key] = raw === null ? cfg.default : cfg.parse ? cfg.parse(raw) : raw;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Always `replace: true` — these are filter/pagination changes, not
  // distinct pages a user would ever want to Back/Forward between
  // individually (that would make Back have to be clicked once per
  // keystroke/page-click to escape a list, which is its own kind of
  // broken). One list visit should be one history entry; only navigating
  // to a detail page (a real `navigate()` push elsewhere) should add one.
  const setState = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            const cfg = schema[key];
            const isDefault = value === cfg.default || value === undefined || value === null || value === "";
            if (isDefault) {
              next.delete(key);
            } else {
              next.set(key, cfg.serialize ? cfg.serialize(value) : String(value));
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams],
  );

  return [state, setState];
}
